import {createSelector} from "@reduxjs/toolkit";

import {hasSpfTable, normalizeSpf, SPF_PARAMS, type SpfColumns} from "@reva/shared/parameter-resolution";
import equal from "fast-deep-equal";

import {
    selectAllInitialValuationParams,
    selectAllInspectionDerivedParams,
    selectAllInspectionParams,
} from "./objectParametersSlice";
import {updateMultipleParamsAndValidateAndSubmitFormThunk} from "./objectParametersThunk";
import {selectReadOnly} from "./persistenceSlice";
import type {RevaThunkAction} from "./revaHooks";
import type {RevaRootState} from "./store";
import {selectEffectiveIndicationData} from "./valueIndicationSelectors";

type Params = Record<string, unknown> | null | undefined;

export const selectSpecialPropertyFeaturesOutput = createSelector(
    [selectEffectiveIndicationData],
    (indicationData: {special_property_feature_list?: unknown[]} | null) =>
        indicationData?.special_property_feature_list?.[0] ?? null,
);

const EMPTY: SpfColumns = {
    [SPF_PARAMS.names]: null,
    [SPF_PARAMS.values]: null,
    [SPF_PARAMS.valuesLending]: null,
    [SPF_PARAMS.shares]: null,
};

/**
 * Merkmale aus der Besichtigung, sonst aus der Erstbewertung. Alle vier Arrays stammen aus derselben Quelle,
 * damit z.B. Beleihungswerte der Erstbewertung nicht an die Merkmale der Besichtigung geraten.
 */
export function spfFromInspectionOrInitialValuation(state: RevaRootState): SpfColumns | null {
    const source = [selectAllInspectionParams(state) as Params, selectAllInitialValuationParams(state) as Params].find(
        hasSpfTable,
    );
    return normalizeSpf(source);
}

/** Würde „Zurücksetzen“ die eigene Tabelle ändern? */
export function selectCanResetSpf(state: RevaRootState): boolean {
    const current = normalizeSpf(selectAllInspectionDerivedParams(state) as Params);
    return current != null && !equal(current, spfFromInspectionOrInitialValuation(state) ?? EMPTY);
}

export const importSpecialPropertyFeatures = (): RevaThunkAction<void> => (dispatch, getState) => {
    const state = getState();
    if (selectReadOnly(state) || hasSpfTable(selectAllInspectionDerivedParams(state) as Params)) return;

    const imported = spfFromInspectionOrInitialValuation(state);
    if (imported) dispatch(updateMultipleParamsAndValidateAndSubmitFormThunk("INSPECTION_DERIVED", imported));
};

/**
 * Kommentar zu den Merkmalen; liegt an special_property_features_values. Kommentare an den übrigen Arrays
 * (Altbestand) werden dabei geleert, sonst tauchen sie nach Löschen dieses Kommentars wieder auf.
 */
export const saveSpfComment =
    (comment: string | null): RevaThunkAction<void> =>
    (dispatch, getState) => {
        const current = selectAllInspectionDerivedParams(getState()) as Params;
        const params = Object.fromEntries(Object.values(SPF_PARAMS).map((p) => [p, current?.[p] ?? null]));
        const comments = Object.fromEntries(
            Object.values(SPF_PARAMS).map((p) => [p, p === SPF_PARAMS.values ? comment : null]),
        );
        dispatch(updateMultipleParamsAndValidateAndSubmitFormThunk("INSPECTION_DERIVED", params, comments));
    };

/** `null` setzt auf Besichtigung/Erstbewertung zurück, `[]` leert die Tabelle. */
export const resetOrEmptySpf =
    (value: null | []): RevaThunkAction<void> =>
    (dispatch, getState) => {
        const params =
            value == null
                ? (spfFromInspectionOrInitialValuation(getState()) ?? EMPTY)
                : {...EMPTY, [SPF_PARAMS.names]: [], [SPF_PARAMS.values]: []};
        dispatch(updateMultipleParamsAndValidateAndSubmitFormThunk("INSPECTION_DERIVED", params));
    };
